<template>
  <div
    v-if="doc"
    :class="doc.controls.bookSidebarClass"
    class="control"
    @click="onClick"
  >
    <div class="control__box">
      <SidebarSettings
        v-if="showTextControls"
        :class="doc.controls.fontsBtnClass"
        :doc="doc"
      />

      <!-- slot for book TOC -->
      <slot></slot>

      <div
        v-if="showTextControls && doc.has_audio"
        :class="doc.controls.playerBtnClass"
        class="control__btn player-btn"
        title="Listen"
        @click="togglePlayer"
      >
        <div class="control__icon-box">
          <span class="control__icon iconfont icontrol-listen"></span>
        </div>
      </div>
      <SidebarFavorite :doc="doc" :class="doc.controls.favoriteBtnClass" />

      <div
        :class="doc.controls.nightModeBtnClass"
        class="control__btn night-btn"
        title="Night mode"
        @click="toggleNightMode"
      >
        <div class="control__icon-box">
          <span :class="doc.controls.nightModeIconClass"></span>
        </div>
      </div>
      <!-- slot for tutorial -->
      <slot name="tutorial"></slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted } from 'vue'

import { DocView } from '@/models/doc'
import { emitter } from '@/services/mitt'
import SidebarFavorite from '@/app/components/doc/reading/SidebarFavorite.vue'
import SidebarSettings from '@/app/components/doc/reading/SidebarSettings.vue'
import { DESKTOP_WIDTH } from '@/init/settings'
import { Mixpanel } from '@/analytics/mixpanel'

const props = withDefaults(
  defineProps<{
    doc: DocView
    // Set to false to hide text controls: font settings, audio player button.
    showTextControls?: boolean
  }>(),
  {
    showTextControls: true,
  },
)

onMounted(async () => {
  // The 'docContentClicked' is emitted in @/app/components/doc/reading/Content.vue and
  // @/app/components/article/Content.vue
  emitter.on('docContentClicked', (event: any) => {
    props.doc.controls.onDocContentClicked(event)
  })
  emitter.on('showBookControlBar', () => {
    props.doc.controls.show()
  })
  emitter.on('windowResize', () => {
    props.doc.controls.onWindowResize()
  })
  emitter.on('toggleNightMode', async () => {
    props.doc.controls.onToggleNightMode()
  })
  // The 'docContentSelectionStarted' is emitted in @/app/components/doc/reading/Annotator.vue
  emitter.on('docContentSelectionStarted', () => {
    // Don't hide controls on desktop screens
    if (window.innerWidth >= DESKTOP_WIDTH) {
      return
    }

    props.doc.controls.hide()
  })
})

function togglePlayer(event: Event): void {
  props.doc.controls.togglePlayer(event)
  Mixpanel.trackSidebarItemClick('Player')
}

async function toggleNightMode(): Promise<void> {
  await props.doc.controls.toggleNightMode()
  Mixpanel.trackSidebarItemClick('Night Mode')
}
</script>
